import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { AttendanceService } from '../../../services/attendance.service';
import { LeaveService } from '../../../services/leave.service';
import { UserService } from '../../../services/user.service';
import { Attendance } from '../../../interfaces/attendance.interface';
import { LeaveApplication, LeaveStatus } from '../../../interfaces/leave.interface';
import { displayUsername } from '../../../utils/userUtils';
import { formatDate, toLocalIsoDate } from '../../../utils/dateUtils';
import StatTile from '../charts/StatTile';
import HStack100 from '../charts/HStack100';
import Columns from '../charts/Columns';
import { isApprovedOn } from './personalDashboardUtils';
import { DashSection, DashEmpty } from './DashSection';

interface Props {
    /** Signed-in username, used to leave the head out of their own team's numbers. */
    username?: string;
    /** Where "Pending leave approvals" links to (the approvals page when the user may open it). */
    approvalsPath: string;
}

interface TeamData {
    ids: string[];
    names: Map<string, string>;
    attendance: Attendance[];
    leaves: LeaveApplication[] | null;
    leavesTruncated: boolean;
}

const fmtTime = (iso: string) => format(parseISO(iso), 'hh:mm a');

/**
 * "My team - today" for department heads. Uses the same scoped calls as Today's Attendance: the
 * roster decides who is in the team, and if it is missing or NOT restricted (i.e. it would be the
 * whole institution) the block renders nothing at all rather than risk showing everyone.
 */
const TeamToday: React.FC<Props> = ({ username, approvalsPath }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<TeamData | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const [rosterR, attR, usersR, leaveR] = await Promise.allSettled([
                AttendanceService.getInstitutionTodayRoster(),
                AttendanceService.getInstitutionTodayAttendance(),
                UserService.getInstitutionUsers(0, 200),
                LeaveService.getAllApplicationsByInstitution(0, 100),
            ]);
            if (cancelled) return;
            if (rosterR.status !== 'fulfilled' || !rosterR.value.restricted || attR.status !== 'fulfilled') {
                setData(null);
                setLoading(false);
                return;
            }
            const users: { id: string; username: string; name?: string | null; fullName?: string | null }[] = usersR.status === 'fulfilled'
                ? ((usersR.value as any).content ?? usersR.value ?? [])
                : [];
            const self = username ? users.find(u => u.username === username) : undefined;
            const inTeam = new Set(rosterR.value.userIds.filter(id => id !== self?.id));
            const names = new Map<string, string>();
            users.forEach(u => { if (inTeam.has(u.id)) names.set(u.id, u.fullName || u.name || displayUsername(u.username)); });
            const leaves = leaveR.status === 'fulfilled'
                ? leaveR.value.content.filter(l => inTeam.has(l.userId)) : null;
            setData({
                ids: Array.from(inTeam),
                names,
                attendance: attR.value.filter(a => inTeam.has(a.userId)),
                leaves,
                leavesTruncated: leaveR.status === 'fulfilled' && leaveR.value.totalElements > leaveR.value.content.length,
            });
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [username]);

    const today = toLocalIsoDate();

    const stats = useMemo(() => {
        if (!data) return null;
        const presentIds = new Set(data.attendance.map(a => a.userId));
        const late = data.attendance.filter(a => a.isLate === true);
        const lateIds = new Set(late.map(a => a.userId));
        const onLeaveIds = new Set((data.leaves ?? []).filter(l => isApprovedOn(l, today)).map(l => l.userId));
        const leaveOnly = data.ids.filter(id => onLeaveIds.has(id) && !presentIds.has(id));
        const notIn = data.ids.filter(id => !presentIds.has(id) && !onLeaveIds.has(id));
        const pending = (data.leaves ?? []).filter(l => l.leaveStatus === LeaveStatus.Pending)
            .sort((a, b) => a.startDate.localeCompare(b.startDate));
        const hours = new Map<number, number>();
        data.attendance.forEach(a => {
            if (!a.startTime) return;
            const h = parseISO(a.startTime).getHours();
            hours.set(h, (hours.get(h) ?? 0) + 1);
        });
        const arrivals = hours.size === 0 ? [] : (() => {
            const keys = Array.from(hours.keys());
            const lo = Math.min(...keys), hi = Math.max(...keys);
            return Array.from({ length: hi - lo + 1 }, (_, i) => ({
                label: `${String(lo + i).padStart(2, '0')}h`, value: hours.get(lo + i) ?? 0,
            }));
        })();
        return {
            size: data.ids.length, present: presentIds.size, late: lateIds.size,
            onTime: presentIds.size - lateIds.size, leave: leaveOnly.length, notIn, pending, arrivals,
        };
    }, [data, today]);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} role="status" aria-label="Loading team"><CircularProgress size={28} /></Box>;
    }
    if (!data || !stats || stats.size === 0) return null;

    const nameOf = (id: string) => data.names.get(id) ?? 'Team member';

    return (
        <Box data-testid="team-today" data-help-section="dashboard.teamToday" sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>My team — today</Typography>
            <Paper variant="outlined" sx={{ p: 1, mb: 2 }} data-help-section="dashboard.teamToday.kpis">
                <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
                    <StatTile data-help="dashboard.teamToday.tile.size" label="Team size" value={stats.size} />
                    <StatTile data-help="dashboard.teamToday.tile.present" label="Present today" value={stats.present} tone={stats.present > 0 ? 'good' : 'default'} />
                    <StatTile data-help="dashboard.teamToday.tile.late" label="Late today" value={stats.late} tone={stats.late > 0 ? 'warning' : 'default'} />
                    {data.leaves && <StatTile data-help="dashboard.teamToday.tile.onLeave" label="On leave today" value={stats.leave} />}
                    <StatTile data-help="dashboard.teamToday.tile.notIn" label="Not in yet" value={stats.notIn.length} tone={stats.notIn.length > 0 ? 'warning' : 'good'} sub="No punch, not on leave" />
                    {data.leaves && (
                        <StatTile data-help="dashboard.teamToday.tile.approvals"
                            label="Leave approvals" value={`${stats.pending.length}${data.leavesTruncated ? '+' : ''}`}
                            tone={stats.pending.length > 0 ? 'warning' : 'default'} sub="Awaiting your team"
                        />
                    )}
                </Stack>
            </Paper>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(2, minmax(0,1fr))' }, gap: 2 }}>
                {stats.size > 0 && (
                    <DashSection data-help-section="dashboard.teamToday.mix" title="Team today" subtitle="On time (green), late (amber), on leave (blue), not in yet (red)">
                        <HStack100
                            rows={[{ label: 'Team', segments: [stats.onTime, stats.late, stats.leave, stats.notIn.length] }]}
                            seriesLabels={['On time', 'Late', 'On leave', 'Not in yet']}
                            colorIndices={[2, 3, 0, 7]}
                        />
                    </DashSection>
                )}
                {stats.arrivals.length > 0 && (
                    <DashSection data-help-section="dashboard.teamToday.arrivals" title="Arrivals today" subtitle="First punch-in by hour">
                        <Columns data={stats.arrivals} tickFmt={n => (Number.isInteger(n) ? String(n) : '')} />
                    </DashSection>
                )}
                <DashSection data-help-section="dashboard.teamToday.notInList" title="Not in yet" subtitle="No punch today and not on approved leave">
                    {stats.notIn.length === 0 ? <DashEmpty text="Everyone is in or on leave." /> : (
                        <Stack spacing={0.5} data-testid="team-not-in">
                            {stats.notIn.slice(0, 8).map(id => (
                                <Typography key={id} variant="body2">{nameOf(id)}</Typography>
                            ))}
                            {stats.notIn.length > 8 && (
                                <Typography variant="caption" color="text.secondary">+ {stats.notIn.length - 8} more</Typography>
                            )}
                        </Stack>
                    )}
                </DashSection>
                {data.leaves && (
                    <DashSection data-help-section="dashboard.teamToday.pending" title="Pending leave approvals" subtitle="Your team, oldest start date first">
                        {stats.pending.length === 0 ? <DashEmpty text="No pending requests." /> : (
                            <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />} data-testid="team-pending">
                                {stats.pending.slice(0, 5).map(l => (
                                    <Stack key={l.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Link component={RouterLink} to={approvalsPath} underline="hover" sx={{ fontWeight: 600 }}>
                                            {data.names.get(l.userId) ?? displayUsername(l.userName)}
                                        </Link>
                                        <Chip size="small" label={l.leaveType} />
                                        <Box sx={{ flex: 1 }} />
                                        <Typography variant="body2">{formatDate(l.startDate)} – {formatDate(l.endDate)}</Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </DashSection>
                )}
            </Box>
        </Box>
    );
};

export default TeamToday;
